import '@pages/Clients/ServicesSorting/styles/ServicesSorting.desktop.scss';
import '@pages/Clients/ServicesSorting/styles/ServicesSorting.tablet.scss';
import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import { faFileExcel } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dropdown, Space } from 'antd';
import { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ADD_CATEGORY_LABEL,
  ADD_CLIENT_SERVICES_LABEL,
  ADD_CLIENT_SERVICES_PATH,
  ADD_SERVICE_CATEGORY_PATH,
  GET_SERVICES_IMPORT_TEMPLATE_ENDPOINT,
  PREFIX,
  RIGHTS_CLIENT_SERVICES,
  RIGHTS_IS_CATEGORY_CREATE,
  RIGHTS_IS_CREATE,
  RIGHTS_IS_EXPORT,
  RIGHTS_IS_IMPORT,
  TABLET,
} from '@app/constants';
import { useAccessRights, useExportToExcel, useWindowSize } from '@app/hooks';
import { HeaderContext } from '@app/providers';
import type { NClientServices } from '@app/types';
import { openImportTemplateFile } from '@app/utils';

import { MobileHeaderDropdown } from '@pages/Clients/ServicesSorting/components/MobileHeaderDropdown';

import type { NServicesSorting } from '@pages/Clients/ServicesSorting/ServicesSorting.types';
import { createServicesSortingMenuItems } from '@pages/Clients/ServicesSorting/utils/createServicesSortingMenuItems';

import type { MenuProps } from 'antd';

export const ServicesSortingHeader = () => {
  const { header: contextHeaderData, isLoading: isHeaderLoading } =
    useContext(HeaderContext);
  const { innerWidth = 0 } = useWindowSize() ?? {};
  const services = useMemo(
    () =>
      (Array.isArray(contextHeaderData?.services)
        ? contextHeaderData.services
        : []) as NServicesSorting.ServiceRow[],
    [contextHeaderData]
  );
  const isMobile = innerWidth <= TABLET;
  const { hasUserRight: hasUserRightToExport } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_EXPORT
  );
  const { hasUserRight: hasUserRightToImport } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_IMPORT
  );
  const { hasUserRight: hasUserRightToCreateService } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_CREATE
  );
  const { hasUserRight: hasUserRightToCreateCategory } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_CATEGORY_CREATE
  );
  const [loader, setLoader] = useState(false);

  const { exportToExcelByIds } = useExportToExcel({
    setLoader,
  });

  const canManageExcel = hasUserRightToExport || hasUserRightToImport;

  const items: MenuProps['items'] = useMemo(
    () =>
      createServicesSortingMenuItems({
        services,
        hasUserRightToExport,
        hasUserRightToImport,
        exportToExcelByIds: (ids) =>
          exportToExcelByIds(ids as NClientServices.Service[]),
        importInputId: 'UpdateWithSortingByExcel',
        exportLabel: (
          <span className="unselectable" data-testid="export-to-file">
            Экспорт в файл
          </span>
        ),
        onExportTemplateClick: () =>
          openImportTemplateFile(GET_SERVICES_IMPORT_TEMPLATE_ENDPOINT),
      }),
    [exportToExcelByIds, hasUserRightToExport, hasUserRightToImport, services]
  );

  return (
    <div className={`${PREFIX}services-sorting-header`}>
      <Space className="header-buttons__desktop">
        {canManageExcel && (
          <Dropdown menu={{ items }}>
            <Button loading={loader || isHeaderLoading}>
              <Space>
                {isMobile ? (
                  <FontAwesomeIcon icon={faFileExcel} size={'lg'} />
                ) : (
                  <Space data-testid="excel-operations">
                    Операции с Excel
                    <DownOutlined />
                  </Space>
                )}
              </Space>
            </Button>
          </Dropdown>
        )}

        {hasUserRightToCreateCategory && (
          <Link to={ADD_SERVICE_CATEGORY_PATH}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              data-testid="add-category"
            >
              {ADD_CATEGORY_LABEL}
            </Button>
          </Link>
        )}

        {hasUserRightToCreateService && (
          <Link to={ADD_CLIENT_SERVICES_PATH}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              data-testid="add-service"
            >
              {ADD_CLIENT_SERVICES_LABEL}
            </Button>
          </Link>
        )}
      </Space>

      <MobileHeaderDropdown
        services={services}
        hasUserRightToExport={hasUserRightToExport}
        hasUserRightToImport={hasUserRightToImport}
        hasUserRightToCreateCategory={hasUserRightToCreateCategory}
        hasUserRightToCreateService={hasUserRightToCreateService}
      />
    </div>
  );
};
